import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

/**
 * 文案表。
 *
 * ⚠ 错误文案按 **`code`** 组织，而不是按界面位置 —— 契约 §2 说得明白：
 *   响应里的 `message` 是**面向开发者**的，不要直接展示给用户。
 *   所以每个出网错误码都要在这里有一条自己的中文，表外的码落 `error.unknown`。
 *
 * ⚠ 键里带点(`error.AUTH.CODE_INVALID`)是有意的：i18next 默认用 `.` 作层级分隔，
 *   于是 `messageKeyOf('AUTH.CODE_INVALID')` 生成的键天然就能查到下面的嵌套结构。
 */

const zhCN = {
  appName: 'Cione',
  common: {
    loading: '正在准备…',
    offline: '连不上服务器，请检查网络后重试',
    retry: '重试',
    cancel: '取消',
    confirm: '确定',
    save: '保存',
    traceHint: '排查编号 {{traceId}}',
  },
  home: {
    // ⚠ 口径：登录前置(2026-09-02 方案 A)。不要再写「现在就能聊」那类先用后登录的话
    greetingAnon: '你好，说说你想去哪儿 —— 点发送时会先请你登录',
    greetingUser: '你好，{{name}}',
    levelAnon: '未登录',
    levelUser: '已登录',
    levelAdmin: '管理员',
    startChat: '开始对话',
    chatPlaceholder: '对话页在 M2 —— 现在这里只验证认证链路',
    myPage: '我的',
    demoRestricted: '试一个需要登录的动作',
    demoRestrictedLabel: '查看我的资料',
    demoDone: '已完成：{{label}}',
  },
  chat: {
    empty: '想去哪儿？直接说就行',
    // 未登录时的空状态。⚠ 要说清「先写、点发送时才登录」，而不是「打开就要登录」
    emptyAnon: '登录后开始对话 —— 先写下你想去哪儿，点发送时会请你登录',
    placeholder: '说点什么…',
    inputLabel: '输入框',
    send: '发送',
    stop: '停止',
    stopping: '停止中',
    thinking: '正在思考…',
    noContent: '（已停止，这一轮没有内容）',
    newConversation: '新对话',
    loginRequired: '这一步需要登录',
    continueLabel: '继续刚才那一步',
    // ⚠ 断流：实测重开同一条 channel 拿不回中断期间的内容，所以只能重发一轮
    interrupted: '连接中断了，这条回答没说完',
    /*
      断流之后正在把这一轮从服务端取回来（`lib/turn-recovery.ts`）。
      ⚠ 措辞不说「正在重连」—— 我们没有在重连，流已经没了；做的是**去库里取**那一轮。
        说成重连的话，取不回来时用户会以为是网络还没好，而其实该点重新发送了。
    */
    recovering: '连接断了，正在把这一轮取回来…',
    resend: '重新发送',
    /*
      ⚠⚠ 历史回放里**解不开**的卡片（W4）。与 `unknownCardDev`（不认识的 type）分开：
        那个是正常降级、只在开发期显示；这个是数据坏了，而历史里**没有文本可退**
        （后端有卡片时话术只剩一句概述），所以必须明说，不能静默跳过。
      ⚠ 文案不写「加载失败，请重试」—— 重试也不会变好，那是永久残缺的一屏。
    */
    brokenCard: '这张卡片无法展示',
    /*
      ⚠⚠ 从历史接回来时的提示。两件事都得说，而且都不是客套 ——
        ① AI 只拿得到最近 10 轮（ai_core `_run_turn` 的 `max_rounds=10`），
           而用户在回看页翻得到全部；
        ② 粘性断点：上次若停在某个选择上，这一句会被当成对它的回答
           （ai_core `_paused()` 直入断点，不经 supervisor）。
      ⚠ 别写成「已恢复对话」这类只报喜的话 —— 那正好把两条限制都藏起来了。
    */
    resumedNotice: '接着这条历史对话聊。AI 只记得最近几轮；如果上次停在某个选择上，它可能接着那一步问。',
    /*
      ⚠⚠ 流上撞到 `AUTH.TOKEN_EXPIRED` 时**专用**的一句（W1-5）。
        不能复用 `error.AUTH.TOKEN_EXPIRED`（「登录已过期，请重新登录」）——
        那句在**请求层**是准的（那条路径上刷新已经试过并失败了），
        在**流**上却是吓人的：access 只有 5 分钟，聊一会儿过期是常态，
        下一个请求会静默续上，用户根本不需要去重新登录。
        照搬那句等于让用户白登一次录，还以为自己被登出了。
      ⚠ 说「再试一次」就必须真有得点 —— 所以气泡上同时给了「重新发送」。
    */
    tokenExpiredRetry: '刚才那句没发出去，再试一次',
    unknownCardDev: '未注册的卡片 type：{{type}}（已降级为文本）',
  },
  /* 结果卡片（W2）。⚠ 卡片上除「点选」外没有任何写操作入口 —— 见 W2 红线。 */
  cards: {
    choose: '选这个',
    flightTitle: '找到 {{count}} 个航班',
    // ⚠ 供应商返回的比展示的多时用这句（option_count > 展示数）；相等时仍用 flightTitle
    flightShowingTop: '共 {{total}} 个方案，展示前 {{count}}',
    hotelTitle: '找到 {{count}} 家酒店',
    nonstop: '直飞',
    stops: '经停 {{count}} 次',
    // ⚠ 只在 1~8 之间显示：9 = 9 个及以上，0 = **未知**（照字面写「剩 0 张」会把还能买的说成卖光了）
    seatsLeft: '剩 {{count}} 张',
    perNight: '{{price}}/晚',
    score: '{{score}} 分',
    hotelUnnamed: '（未提供名称）',
    /*
      ⚠ 这一条是**发出去的那句话**，会作为用户气泡出现在对话里，所以它要跟界面语言走。
      ⚠ 后端酒店选择靠 **LLM 抽槽**（`hotel/agent.py` 抽出 `hotel_index`），
        不是正则，所以说法不必死板 —— 但**必须带上序号**：
        `_pick_hotel` 只认 `hotel_index`（名字只是兜底的模糊匹配）。
      ⚠ 机票那张卡刚好相反：`_match_choice` 是**真正则**，所以那边发的是航班号本身，
        不走 i18n（见 `flight-options.tsx`）。
      ⚠ 2026-09-03 曾因后端正则吃不下含数字的航司码，在这里加过一条
        `chooseFlightByIndexUtterance`（「第 N 个」）临时降级；后端修好后已删。
        **别再加回来** —— 理由记在 `flight-options.tsx` 头注里。
    */
    chooseHotelUtterance: '第 {{idx}} 家',
    /*
      `flight_traveler_required`（机票域等待点 ⑤）。
      ⚠ `message` 后端一般会给，这两条只是兜底 —— 缺字段不许白屏。
      ⚠ 人数缺失时用不带人数那条：**说错人数比不说更糟**。
    */
    travelerRequired: '还没有乘机人，先去添加一位再继续',
    travelerRequiredNeed: '本次需要 {{count}} 位乘机人，先去添加再继续',
    addTraveler: '去添加乘机人',
    travelerAdded: '加好了，继续',
    /*
      ⚠ 这是**发出去的那句话**，会作为用户气泡上屏，所以跟界面语言走。
      ⚠ 内容不重要、但**不能踩后端的放弃词表**：`_ABORT_RE` 认「算了|取消|不买|不要了|
        不订|先不|不用了|不确认|不了$」—— 命中就直接收尾、整轮作废。「加好了」是安全的。
      ⚠ 后端拿到任何一句话都会**重查花名册**再决定（`_await_roster` 的注释：
        判据只能是花名册本身），所以这句话措辞不参与判定。
    */
    travelerAddedUtterance: '加好了',
    /*
      `flight_confirmation`（金额确认闸门，2026-09-04）。
      ⚠ 这张卡只做「摘要 + 按钮」：航班/行李/退改的全文在上面那个气泡里，这里**不重排**。
    */
    quoteValidUntil: '报价有效期至 {{at}}，过期可能需要重新验价',
    confirmBuy: '确认购买',
    /* ⚠ 金额写进按钮：用户按下去时看着的就是要付的那个数 */
    confirmBuyWithPrice: '确认购买 {{price}}',
    /* 两步确认展开后那颗键。⚠ 与 `common.confirm`（「确定」）分开写：这一颗是要花钱的 */
    confirmBuyFinal: '确定，下单',
    changeFlight: '换一个',
    notNow: '暂不购买',
    /*
      ⚠⚠ 下面三句是**发出去的那句话**，会作为用户气泡上屏。
      ⚠⚠ 它们同时要被后端 `classify_pause_reply` 认出来，**措辞不能随手改**：
        `确认` 命中 `_CONFIRM_RE`；`换一个` 命中 `_CHANGE_RE`（且不含「换成/改成」，
        不会被判成"改搜索条件"而回去重搜）；`算了` 命中 `_ABORT_RE`。
        改成「再看看」这类会落进 `vague` —— 连着两次含糊后**整轮作废**。
      ⚠ 英文界面下这三句**仍然发中文**（见 en 侧同名键）：后端只认中文词表，
        翻过去等于按钮点了没用。这不是漏翻，是故意的。
    */
    confirmUtterance: '确认',
    changeUtterance: '换一个',
    abortUtterance: '算了',
  },
  login: {
    title: '登录 Cione',
    subtitle: '登录后回到刚才的位置，写好的内容不会丢',
    // 节点级卡片(auth_login_required)那条路径登录后确实会自动继续，所以这句仍然对
    pendingHint: '登录后将继续：{{label}}',
    // ⚠ 发送拦截那条路径用这句：登录后**不自动发送**，用户自己再点一次(产品第 3 点)
    draftKeptHint: '登录后回到你刚才写的内容，你再点一次发送就发出去',
    phone: '手机号',
    phonePlaceholder: '请输入手机号',
    smsCode: '验证码',
    smsCodePlaceholder: '6 位数字',
    password: '密码',
    passwordPlaceholder: '至少 8 位',
    sendCode: '获取验证码',
    resendIn: '{{seconds}} 秒后可重发',
    bySms: '短信验证码登录',
    byPassword: '密码登录',
    submit: '登录',
    close: '以后再说',
    codeSent: '验证码已发送',
  },
  me: {
    title: '我的',
    nickname: '昵称',
    nicknamePlaceholder: '还没有设置',
    phone: '手机号',
    userId: '用户 id',
    level: '身份',
    setPassword: '设置 / 修改密码',
    setPasswordHint: '需要短信验证码二次确认',
    confirmPhone: '手机号',
    confirmPhonePlaceholder: '请填完整手机号（{{masked}}）',
    newPassword: '新密码',
    logout: '退出登录',
    saved: '已保存',
    passwordSaved: '密码已更新',
    anonymousBlocked: '登录后才能管理资料',
    travelers: '出行人',
    orders: '我的订单',
  },
  travelers: {
    title: '出行人',
    subtitle: '常用乘机人，下单时可直接选择',
    empty: '还没有出行人，先添加一位',
    add: '添加出行人',
    edit: '编辑',
    // ⚠ 删除是**归档**（已有订单的退改仍用得到），别写「永久删除」
    remove: '移出花名册',
    removeConfirm: '把「{{name}}」移出花名册？已有订单的退改仍可使用，不是永久删除。',
    searchPlaceholder: '按姓名搜索',
    shared: '企业共享',
    name: '姓名',
    docType: '证件类型',
    docNumber: '证件号',
    docNumberMasked: '证件号',
    // ⚠ 编辑页证件号只读；要改是显式「更换证件」，且类型与号必须同时给
    changeDoc: '更换证件',
    changeDocHint: '更换证件类型必须同时填写新的证件号',
    phone: '手机号',
    // ⚠ 编辑时「留空 = 保持不变」，与常规「整包提交」相反
    keepUnchangedHint: '留空的字段保持不变，不会被清空',
    keepPhonePlaceholder: '当前 {{masked}}，留空保持不变',
    surnamePinyin: '姓（拼音）',
    givenNamePinyin: '名（拼音）',
    birthday: '出生日期',
    gender: '性别',
    nationality: '国籍（两位码）',
    docValidTo: '证件有效期至',
    save: '保存',
    saved: '已保存',
    created: '已添加',
    removed: '已移出花名册',
    // ★ 明文纪律：只用于出票、不展示、不缓存
    plaintextHint: '证件号仅用于出票，不会被展示或缓存',
    genderMale: '男',
    genderFemale: '女',
    required: '姓名、证件类型与证件号为必填',
  },
  history: {
    title: '历史对话',
    empty: '还没有历史对话',
    // ⚠ 会话标题就是用户说的第一句话（后端按 seq=1 取），可能什么都没有
    untitled: '（无标题）',
    messageCount: '{{count}} 条',
    open: '查看',
    remove: '删除对话',
    // ⚠ 软删，但整段对话都不再出现 —— 别写「归档」，那是出行人那边的语义
    removeConfirm: '删除',
    removeDone: '已删除',
    // ⚠⚠ 删的是当前正聊着的那条：界面已就地清空，得说一声，否则用户以为消息自己没了
    removedCurrent: '已删除，当前对话已清空',
    replayTitle: '回看',
    // ⚠ 出口就挨着「这里是回看」那句给 —— 只说做不了、不说去哪做，等于把人扔在半路
    continueHere: '在这条对话里继续',
    // ⚠ 空数组 = 到头了，不是出错
    earlier: '加载更早',
    noEarlier: '没有更早的了',
    notFound: '找不到这条对话',
    back: '返回列表',
    // ⚠ 只读快照：模型侧只记得最近若干轮，给「接着聊」会让人以为 AI 全都记得
    readOnly: '这里是回看，发消息请回到对话页',
  },
  orders: {
    title: '我的订单',
    empty: '还没有订单',
    all: '全部',
    detail: '订单详情',
    orderNo: '订单号',
    createdAt: '下单时间',
    amount: '金额',
    supplierOrderNo: '供应商订单号',
    // ⚠ 未支付时供应商订单号为空是正常的
    notIssuedYet: '出票后生成',
    cancel: '取消订单',
    // ⚠ 取消 ≠ 退票：仅出票前撤单
    cancelHint: '仅出票前撤单；已出票需退票请联系客服',
    cancelConfirm: '确定取消这个订单吗？',
    cancelReason: '取消原因（可选）',
    cancelDone: '已提交，当前状态：{{state}}',
    notFound: '找不到这个订单',
    payloadTitle: '行程信息',
    // ⚠ domainPayload parse 失败要降级，不白屏
    payloadBroken: '行程信息暂时无法显示',
    back: '返回列表',
    state: {
      ORDER_CREATED: '已创建',
      ORDER_PENDING_PAYMENT: '待支付',
      ORDER_PAID: '已支付',
      ORDER_FULFILLING: '出票中',
      ORDER_COMPLETED: '已完成',
      ORDER_CANCELLED: '已取消',
      ORDER_REFUNDING: '退款中',
      ORDER_REFUNDED: '已退款',
      ORDER_FAILED: '已失败',
    },
  },
  docTypes: {
    IC: '身份证',
    PP: '护照',
    GA: '港澳通行证',
    TB: '台胞证',
    OTHER: '其他证件',
  },
  error: {
    unknown: '出了点问题，请稍后再试',
    AUTH: {
      LOGIN_REQUIRED: '请先登录',
      TOKEN_EXPIRED: '登录已过期，请重新登录',
      TOKEN_INVALID: '登录状态失效，请重新登录',
      CODE_INVALID: '验证码不正确',
      CODE_EXPIRED: '验证码已过期，请重新获取',
      CODE_TOO_FREQUENT: '发送太频繁，请稍后再试',
      PASSWORD_INVALID: '手机号或密码不正确',
      ACCOUNT_DISABLED: '账号已被停用，请联系客服',
      ACTION_FORBIDDEN: '这一步需要登录后才能继续',
    },
    COMMON: {
      INTERNAL: '服务出了点问题，请稍后再试',
      INVALID_ARGUMENT: '填写的内容有误，请检查后重试',
      FAILED_PRECONDITION: '当前状态下无法完成这个操作',
      UNAUTHENTICATED: '请先登录',
      FORBIDDEN: '没有权限进行这个操作',
      NOT_FOUND: '找不到对应的内容',
      CONFLICT: '状态已经变了，请刷新后重试',
      RATE_LIMITED: '当前访问量较大，请稍后再试',
      UPSTREAM_UNAVAILABLE: '上游服务暂时不可用，请稍后再试',
    },
    IDENTITY: {
      USER_NOT_FOUND: '找不到这个用户',
      EMAIL_EXISTS: '这个邮箱已经被使用',
      VERSION_CONFLICT: '资料已被其他设备修改，请刷新后重试',
      DOC_INVALID: '证件信息有误，请核对后重填',
      TRAVELER_NOT_FOUND: '找不到这位出行人',
      TRAVELER_FORBIDDEN: '没有权限查看这位出行人',
      TRAVELER_ARCHIVED: '这位出行人已归档',
      VAULT_NOT_CONFIGURED: '服务端配置缺失，请联系技术支持',
      AUDIT_WRITE_FAILED: '操作未能留痕，已中止',
    },
    TRADE: {
      // ⚠ 文案方向都照契约 §3.4 定，别写绝对：
      //   INVENTORY_GONE 是「认不出的拒绝」的兜底，不能断定一定没座位 → 别写「已售罄」；
      INVENTORY_GONE: '这班订不到了，换一班看看',
      //   PRICE_CHANGED 说不出新价、也不知方向 → 别写「涨价了」；
      PRICE_CHANGED: '价格有变动，请重新确认这一班',
      QUOTE_EXPIRED: '报价已过期，请重新搜索',
      TRAVELER_DOC_INVALID: '证件信息有问题，请到出行人里核对',
      // ⚠ 文案必须明确「不要重发」：供应商侧可能已经成功，重发会重复扣款
      RESULT_UNKNOWN: '结果待确认，请勿重复提交 —— 请到订单里查看最新状态',
      //   UNAVAILABLE 多发生在出票阶段、用户已付款 → 绝不能是「换一班」
      UNAVAILABLE: '暂时无法出票，请联系客服',
    },
    CLIENT: {
      NETWORK: '网络不可用，请检查连接后重试',
      TIMEOUT: '请求超时，请稍后再试',
      ABORTED: '操作已取消',
      CONTRACT_DRIFT: '客户端与服务端版本不一致，请更新后重试',
    },
  },
} as const

const en = {
  appName: 'Cione',
  common: {
    loading: 'Getting ready…',
    offline: 'Cannot reach the server. Check your connection and retry.',
    retry: 'Retry',
    cancel: 'Cancel',
    confirm: 'OK',
    save: 'Save',
    traceHint: 'Trace {{traceId}}',
  },
  home: {
    greetingAnon: 'Hi — tell me where you want to go. Sending asks you to sign in first.',
    greetingUser: 'Hi, {{name}}',
    levelAnon: 'Not signed in',
    levelUser: 'Signed in',
    levelAdmin: 'Administrator',
    startChat: 'Start chatting',
    chatPlaceholder: 'The chat page lands in M2 — this screen only verifies auth.',
    myPage: 'Profile',
    demoRestricted: 'Try an action that needs sign-in',
    demoRestrictedLabel: 'Open my profile',
    demoDone: 'Done: {{label}}',
  },
  chat: {
    empty: 'Where to? Just say it.',
    emptyAnon: 'Sign in to start. Write where you want to go — sending asks you to sign in.',
    placeholder: 'Say something…',
    inputLabel: 'Message',
    send: 'Send',
    stop: 'Stop',
    stopping: 'Stopping',
    thinking: 'Thinking…',
    noContent: '(Stopped — nothing came back)',
    newConversation: 'New chat',
    loginRequired: 'This step requires signing in',
    continueLabel: 'continue where you left off',
    interrupted: 'The connection dropped; this answer is incomplete.',
    /* Not "reconnecting" — the stream is gone; we're fetching the turn the server already saved. */
    recovering: 'Connection dropped — fetching this answer from the server…',
    resend: 'Send again',
    brokenCard: 'This card cannot be shown',
    resumedNotice:
      'Continuing a past chat. The assistant only remembers the last few turns; if it was waiting on a choice, your next message may be read as the answer.',
    tokenExpiredRetry: "That one didn't go through — give it another try",
    unknownCardDev: 'Unregistered card type: {{type}} (degraded to text)',
  },
  cards: {
    choose: 'Choose',
    flightTitle: '{{count}} flights found',
    flightShowingTop: 'Top {{count}} of {{total}} flights',
    hotelTitle: '{{count}} hotels found',
    nonstop: 'Nonstop',
    stops: '{{count}} stop(s)',
    seatsLeft: '{{count}} left',
    perNight: '{{price}}/night',
    score: '{{score}}/5',
    hotelUnnamed: '(no name)',
    /* ⚠ Must keep the index — the backend slot extractor looks for `hotel_index`. */
    chooseHotelUtterance: 'Hotel #{{idx}}',
    /* `flight_traveler_required` fallbacks — never blank on a missing field. */
    travelerRequired: 'Add a passenger before we can continue',
    travelerRequiredNeed: 'This booking needs {{count}} passenger(s) — add them to continue',
    addTraveler: 'Add a passenger',
    travelerAdded: "Done — let's continue",
    /* ⚠ Sent as the user's own message. Must NOT match the backend abort word list. */
    travelerAddedUtterance: 'Added',
    /* `flight_confirmation` — summary + buttons only; the full text is in the bubble above. */
    quoteValidUntil: 'Price held until {{at}} — after that it may need re-pricing',
    confirmBuy: 'Confirm purchase',
    confirmBuyWithPrice: 'Confirm purchase {{price}}',
    confirmBuyFinal: 'Yes, book it',
    changeFlight: 'Pick another',
    notNow: 'Not now',
    /*
      ⚠⚠ These three stay in Chinese **on purpose**, unlike every other utterance here.
        The backend gate (`classify_pause_reply`) matches Chinese words only —
        translating them makes the buttons do nothing. Not a missing translation.
    */
    confirmUtterance: '确认',
    changeUtterance: '换一个',
    abortUtterance: '算了',
  },
  login: {
    title: 'Sign in to Cione',
    subtitle: "You'll come back to what you wrote — nothing is lost.",
    pendingHint: 'After signing in: {{label}}',
    draftKeptHint: "After signing in you'll be back at what you wrote — tap send again.",
    phone: 'Phone',
    phonePlaceholder: 'Your phone number',
    smsCode: 'Code',
    smsCodePlaceholder: '6 digits',
    password: 'Password',
    passwordPlaceholder: 'At least 8 characters',
    sendCode: 'Send code',
    resendIn: 'Resend in {{seconds}}s',
    bySms: 'Sign in with SMS code',
    byPassword: 'Sign in with password',
    submit: 'Sign in',
    close: 'Not now',
    codeSent: 'Code sent',
  },
  me: {
    title: 'Profile',
    nickname: 'Nickname',
    nicknamePlaceholder: 'Not set',
    phone: 'Phone',
    userId: 'User id',
    level: 'Level',
    setPassword: 'Set / change password',
    setPasswordHint: 'Requires an SMS code',
    confirmPhone: 'Phone',
    confirmPhonePlaceholder: 'Type your full number ({{masked}})',
    newPassword: 'New password',
    logout: 'Sign out',
    saved: 'Saved',
    passwordSaved: 'Password updated',
    anonymousBlocked: 'Sign in to manage your profile',
    travelers: 'Travelers',
    orders: 'Orders',
  },
  travelers: {
    title: 'Travelers',
    subtitle: 'Frequent passengers, selectable at booking',
    empty: 'No travelers yet — add one',
    add: 'Add traveler',
    edit: 'Edit',
    remove: 'Remove from roster',
    removeConfirm: 'Remove “{{name}}” from the roster? Existing orders can still use it — this is not a permanent delete.',
    searchPlaceholder: 'Search by name',
    shared: 'Shared',
    name: 'Name',
    docType: 'Document type',
    docNumber: 'Document number',
    docNumberMasked: 'Document number',
    changeDoc: 'Change document',
    changeDocHint: 'Changing the document type requires the new document number too',
    phone: 'Phone',
    keepUnchangedHint: 'Fields left blank keep their current value',
    keepPhonePlaceholder: 'Currently {{masked}}, blank keeps it',
    surnamePinyin: 'Surname (pinyin)',
    givenNamePinyin: 'Given name (pinyin)',
    birthday: 'Date of birth',
    gender: 'Gender',
    nationality: 'Nationality (2-letter)',
    docValidTo: 'Document valid until',
    save: 'Save',
    saved: 'Saved',
    created: 'Added',
    removed: 'Removed from roster',
    plaintextHint: 'The document number is used only for ticketing — never shown or cached',
    genderMale: 'Male',
    genderFemale: 'Female',
    required: 'Name, document type and number are required',
  },
  history: {
    title: 'History',
    empty: 'No past conversations',
    untitled: '(untitled)',
    messageCount: '{{count}} messages',
    open: 'Open',
    remove: 'Delete',
    removeConfirm: 'Delete',
    removeDone: 'Deleted',
    removedCurrent: 'Deleted. The current chat was cleared.',
    replayTitle: 'Replay',
    continueHere: 'Continue this chat',
    earlier: 'Load earlier',
    noEarlier: 'No earlier messages',
    notFound: 'Conversation not found',
    back: 'Back to list',
    readOnly: 'Read-only replay — go back to the chat to send messages',
  },
  orders: {
    title: 'Orders',
    empty: 'No orders yet',
    all: 'All',
    detail: 'Order detail',
    orderNo: 'Order no.',
    createdAt: 'Created',
    amount: 'Amount',
    supplierOrderNo: 'Supplier order no.',
    notIssuedYet: 'After ticketing',
    cancel: 'Cancel order',
    cancelHint: 'Pre-ticketing cancellation only; contact support for refunds after ticketing',
    cancelConfirm: 'Cancel this order?',
    cancelReason: 'Reason (optional)',
    cancelDone: 'Submitted. Current status: {{state}}',
    notFound: 'Order not found',
    payloadTitle: 'Itinerary',
    payloadBroken: 'Itinerary is temporarily unavailable',
    back: 'Back to list',
    state: {
      ORDER_CREATED: 'Created',
      ORDER_PENDING_PAYMENT: 'Pending payment',
      ORDER_PAID: 'Paid',
      ORDER_FULFILLING: 'Ticketing',
      ORDER_COMPLETED: 'Completed',
      ORDER_CANCELLED: 'Cancelled',
      ORDER_REFUNDING: 'Refunding',
      ORDER_REFUNDED: 'Refunded',
      ORDER_FAILED: 'Failed',
    },
  },
  docTypes: {
    IC: 'ID card',
    PP: 'Passport',
    GA: 'HK/Macau permit',
    TB: 'Taiwan permit',
    OTHER: 'Other',
  },
  error: {
    unknown: 'Something went wrong. Please try again.',
    AUTH: {
      LOGIN_REQUIRED: 'Please sign in first',
      TOKEN_EXPIRED: 'Your session expired. Please sign in again.',
      TOKEN_INVALID: 'Your session is no longer valid. Please sign in again.',
      CODE_INVALID: 'That code is not correct',
      CODE_EXPIRED: 'That code expired. Please request a new one.',
      CODE_TOO_FREQUENT: 'Too many requests. Please wait a moment.',
      PASSWORD_INVALID: 'Phone or password is incorrect',
      ACCOUNT_DISABLED: 'This account is disabled. Please contact support.',
      ACTION_FORBIDDEN: 'This step requires signing in',
    },
    COMMON: {
      INTERNAL: 'Server error. Please try again later.',
      INVALID_ARGUMENT: 'Some input is invalid. Please check and retry.',
      FAILED_PRECONDITION: 'This action is not possible in the current state',
      UNAUTHENTICATED: 'Please sign in first',
      FORBIDDEN: 'You do not have permission for this action',
      NOT_FOUND: 'Not found',
      CONFLICT: 'State has changed. Please refresh and retry.',
      RATE_LIMITED: 'Busy right now. Please try again shortly.',
      UPSTREAM_UNAVAILABLE: 'An upstream service is unavailable. Please retry later.',
    },
    IDENTITY: {
      USER_NOT_FOUND: 'User not found',
      EMAIL_EXISTS: 'That email is already in use',
      VERSION_CONFLICT: 'Modified elsewhere. Please refresh and retry.',
      DOC_INVALID: 'Document details are invalid',
      TRAVELER_NOT_FOUND: 'Traveler not found',
      TRAVELER_FORBIDDEN: 'No permission for this traveler',
      TRAVELER_ARCHIVED: 'This traveler is archived',
      VAULT_NOT_CONFIGURED: 'Server configuration missing. Contact support.',
      AUDIT_WRITE_FAILED: 'The action could not be audited and was aborted',
    },
    TRADE: {
      INVENTORY_GONE: 'This flight is no longer bookable — try another one',
      PRICE_CHANGED: 'The price has changed. Please confirm this flight again',
      QUOTE_EXPIRED: 'The quote has expired. Please search again',
      TRAVELER_DOC_INVALID: 'There is a problem with the document — check it in Travelers',
      RESULT_UNKNOWN: 'Result pending — do NOT resubmit. Check your orders for the latest status.',
      UNAVAILABLE: 'Ticketing is temporarily unavailable. Please contact support',
    },
    CLIENT: {
      NETWORK: 'Network unavailable. Check your connection.',
      TIMEOUT: 'Request timed out. Please try again.',
      ABORTED: 'Cancelled',
      CONTRACT_DRIFT: 'Client and server versions differ. Please update.',
    },
  },
} as const

void i18n.use(initReactI18next).init({
  resources: { 'zh-CN': { translation: zhCN }, en: { translation: en } },
  lng: 'zh-CN',
  fallbackLng: 'zh-CN',
  interpolation: { escapeValue: false },
})

export default i18n
export { zhCN, en }
